import React, { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import logo from '@/assets/logo.png'
import { Home, LogOut, X, BookOpen, ChevronDown, ChevronRight, BookA, AudioWaveform, Library, Route, Sparkles, Layers, Workflow } from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const navItems = [
  { label: 'Tổng quan', icon: Home, path: '/', end: true },
]

export const AppSidebar: React.FC<AppSidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const isDictRoute = location.pathname.startsWith('/dict-')
  const isReadingRoute = location.pathname === '/sor-words' || location.pathname.startsWith('/sor-readings') || location.pathname === '/reading-activities'
  const isPronunciationRoute = location.pathname.startsWith('/pronunciation-lab')
  const isPassageDetailRoute = location.pathname.startsWith('/passage/')
  const isLexicalRoute = location.pathname.startsWith('/lexicals') || isPassageDetailRoute
  const isContentRoute = location.pathname.startsWith('/english-content') || location.pathname.startsWith('/workflows')
  const isWorkflowRoute = location.pathname.startsWith('/workflows')
  const [isDictOpen, setIsDictOpen] = useState(isDictRoute)
  const [isReadingOpen, setIsReadingOpen] = useState(isReadingRoute)
  const [isPronunciationOpen, setIsPronunciationOpen] = useState(isPronunciationRoute)
  const [isContentOpen, setIsContentOpen] = useState(isContentRoute)

  useEffect(() => {
    if (isDictRoute) {
      setIsDictOpen(true)
    }
  }, [isDictRoute])

  useEffect(() => {
    if (isReadingRoute) {
      setIsReadingOpen(true)
    }
  }, [isReadingRoute])

  useEffect(() => {
    if (isPronunciationRoute) setIsPronunciationOpen(true)
  }, [isPronunciationRoute])

  useEffect(() => { if (isContentRoute) setIsContentOpen(true) }, [isContentRoute])

  const handleLogout = async () => {
    await logout()
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:relative lg:translate-x-0 lg:z-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="px-4 border-b border-gray-200 flex items-center justify-between h-[60px]">
          <a href="https://hocnhe.com" className="flex items-center gap-2 text-left">
            <img src={logo} alt="Học Nhé" className="w-8 h-8 object-contain" />
            <div className="flex flex-col">
              <span className="text-[15px] font-black tracking-tight leading-none text-[#0068FF]">
                Học Nhé
              </span>
              <span className="text-[9px] text-[#E65F00] font-extrabold tracking-wider mt-0.5 uppercase">
                Học cùng nhau
              </span>
            </div>
          </a>
          <button onClick={onClose} className="lg:hidden p-1 rounded-md hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50/50 text-[#0068FF]'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {item.label}
            </NavLink>
          ))}

          <div className="space-y-1">
            <button onClick={() => setIsPronunciationOpen(!isPronunciationOpen)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isPronunciationRoute ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-100'}`}>
              <span className="flex items-center gap-3"><AudioWaveform className="w-5 h-5 shrink-0 text-gray-500" />Lab phát âm</span>
              {isPronunciationOpen ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
            </button>
            {isPronunciationOpen && <div className="ml-6 mt-1 space-y-1 border-l border-gray-100 pl-4">
              <NavLink to="/pronunciation-lab/sentence" onClick={onClose} className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>Đoạn dài</NavLink>
              <NavLink to="/pronunciation-lab/word" onClick={onClose} className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>Từ ngắn</NavLink>
              <NavLink to="/pronunciation-lab/phoneme" onClick={onClose} className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>Âm vị</NavLink>
            </div>}
          </div>

          {/* Collapsible Reading Menu */}
          <div className="space-y-1 pt-2">
            <button
              onClick={() => setIsReadingOpen(!isReadingOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isReadingOpen || isReadingRoute ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 shrink-0 text-gray-500" />
                <span>Luyện đọc</span>
              </div>
              {isReadingOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {isReadingOpen && (
              <div className="pl-4 space-y-1 border-l border-gray-100 ml-6 mt-1">
                <NavLink
                  to="/sor-words"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Từ vựng
                </NavLink>
                <NavLink
                  to="/sor-readings"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Bài đọc
                </NavLink>
                <NavLink
                  to="/sor-lessons"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive || location.pathname.startsWith('/sor-lessons')
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Bài học
                </NavLink>
                <NavLink
                  to="/reading-activities"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Hoạt động
                </NavLink>
              </div>
            )}
          </div>

          {/* Collapsible Dictionary Menu */}
          <div className="space-y-1 pt-2">
            <button
              onClick={() => setIsDictOpen(!isDictOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isDictOpen || isDictRoute ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <BookA className="w-5 h-5 shrink-0 text-gray-500" />
                <span>Từ điển</span>
              </div>
              {isDictOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {isDictOpen && (
              <div className="pl-4 space-y-1 border-l border-gray-100 ml-6 mt-1">
                <NavLink
                  to="/dict-phonemes"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Phonemes
                </NavLink>
                <NavLink
                  to="/dict-graphemes"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Graphemes
                </NavLink>
                <NavLink
                  to="/dict-words"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Words
                </NavLink>
              </div>
            )}
          </div>

          <div className="space-y-1 pt-2">
            <NavLink
              to="/lexicals/passages"
              onClick={onClose}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive || isLexicalRoute ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <Library className="h-5 w-5 shrink-0 text-gray-500" />
              <span>Kho ngữ liệu</span>
            </NavLink>
            {isLexicalRoute && (
              <div className="ml-8 space-y-1 border-l border-gray-200 pl-2">
                <NavLink to="/lexicals/passages" onClick={onClose} className={({ isActive }) => `block rounded-lg px-3 py-2 text-xs font-semibold ${isActive || isPassageDetailRoute ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>
                  Passages
                </NavLink>
                <NavLink to="/lexicals/sentences" onClick={onClose} className={({ isActive }) => `block rounded-lg px-3 py-2 text-xs font-semibold ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>
                  Sentences
                </NavLink>
                <NavLink to="/lexicals/items" onClick={onClose} className={({ isActive }) => `block rounded-lg px-3 py-2 text-xs font-semibold ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>
                  Lexicals
                </NavLink>
                <NavLink to="/lexicals/taxonomies" onClick={onClose} className={({ isActive }) => `block rounded-lg px-3 py-2 text-xs font-semibold ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>
                  Taxonomies
                </NavLink>
                <NavLink to="/lexicals/roadmaps" onClick={onClose} className={({ isActive }) => `flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold ${isActive ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-600 hover:bg-gray-50'}`}>
                  <Route className="h-3.5 w-3.5" />
                  Roadmaps
                </NavLink>
              </div>
            )}
          </div>

          {/* Shared English content authoring */}
          <div className="space-y-1 pt-2">
            <button
              onClick={() => setIsContentOpen(!isContentOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isContentOpen || isContentRoute ? 'bg-blue-50/50 text-[#0068FF]' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 shrink-0 text-blue-600" />
                <span className="font-bold">Nghiệp vụ</span>
              </div>
              {isContentOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {isContentOpen && (
              <div className="pl-4 space-y-1 border-l border-gray-100 ml-6 mt-1">
                <NavLink
                  to="/english-content/taxonomies"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <Layers className="w-3.5 h-3.5" />
                  Phân loại
                </NavLink>
                <NavLink
                  to="/english-content/lexicals"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <BookA className="w-3.5 h-3.5" />
                  Từ vựng
                </NavLink>
                <NavLink
                  to="/english-content/sentences"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  Câu
                </NavLink>
                <NavLink
                  to="/english-content/readings"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <Library className="w-3.5 h-3.5" />
                  Bài đọc
                </NavLink>
                <NavLink
                  to="/workflows/reading"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      isActive || isWorkflowRoute
                        ? 'bg-blue-50/50 text-[#0068FF]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <Workflow className="w-3.5 h-3.5" />
                  Soạn bài đọc
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        <div className="px-3 py-4 border-t border-gray-200">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full object-cover border border-[#E65F00]"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border border-[#E65F00]">
                  <span className="text-xs font-bold text-white">
                    {(user.display_name || user.email).charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.display_name || user.email}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            Đăng xuất
          </button>
        </div>
      </aside>
    </>
  )
}
